/**
 * Location hours — the portal-side model of `Store.openingHours`.
 *
 * The API speaks `{ monday: { open: 'HHMM', close: 'HHMM', closed } … }`
 * (vendor-api `openingHoursToHours`) and stores `{ monday: '0800-2000' }`,
 * the same shape the Mapbox adapter writes and the consumer store finder
 * reads. Times are 24h "HHMM"; the pickers show 12h labels.
 */
export const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
export type DayKey = (typeof DAY_KEYS)[number];

export interface DayHours {
  open?: string;
  close?: string;
  closed: boolean;
}
export type WeekHours = Record<DayKey, DayHours>;

/** 30-minute steps, 05:00 → 23:30, as { value: 'HHMM', label: '8:00 am' }. */
export const TIME_OPTIONS: Array<{ value: string; label: string }> = (() => {
  const out: Array<{ value: string; label: string }> = [];
  for (let m = 5 * 60; m <= 23 * 60 + 30; m += 30) {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    out.push({ value: `${String(h).padStart(2, '0')}${String(mm).padStart(2, '0')}`, label: fmtTime12(h, mm) });
  }
  return out;
})();

export function fmtTime12(h: number, mm: number): string {
  const suffix = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return mm === 0 ? `${h12}:00 ${suffix}` : `${h12}:${String(mm).padStart(2, '0')} ${suffix}`;
}

export function fmtHHMM(hhmm: string | undefined): string {
  if (!hhmm || !/^\d{4}$/.test(hhmm)) return '';
  return fmtTime12(Number(hhmm.slice(0, 2)), Number(hhmm.slice(2)));
}

/** The default week a new location starts with: Mon–Sat 8–8, Sunday closed. */
export function defaultWeek(): WeekHours {
  const w = {} as WeekHours;
  for (const d of DAY_KEYS) w[d] = d === 'sunday' ? { closed: true } : { open: '0800', close: '2000', closed: false };
  return w;
}

/** Fill any missing day from the API shape so the editor always has 7 rows. */
export function normalizeWeek(raw: Partial<Record<DayKey, DayHours>> | null | undefined): WeekHours {
  const w = {} as WeekHours;
  for (const d of DAY_KEYS) {
    const v = raw?.[d];
    w[d] = v && !v.closed && v.open && v.close ? { open: v.open, close: v.close, closed: false } : { closed: true };
  }
  return w;
}

/** "Open · 8 am – 8 pm" / "Closed today" — L1's HOURS TODAY column. */
export function hoursTodayLabel(week: WeekHours, now: Date = new Date()): { open: boolean; text: string } {
  const idx = (now.getDay() + 6) % 7; // JS Sun=0 → Mon=0
  const d = week[DAY_KEYS[idx]];
  if (!d || d.closed || !d.open || !d.close) return { open: false, text: '' };
  return { open: true, text: `${fmtHHMM(d.open)} – ${fmtHHMM(d.close)}` };
}
